import { Component, Input, Output, EventEmitter, HostListener, ElementRef, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, filter } from 'rxjs';

@Component({
  selector: 'app-header-profile-popup',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div *ngIf="isOpen" 
      class="absolute right-0 top-full mt-3 w-52 bg-white rounded-2xl shadow-[0_12px_40px_-8px_rgba(0,0,0,0.12)] border border-slate-100 py-1.5 z-50 animate-popup overflow-hidden">
      
      <!-- List Items -->
      <div class="px-1.5 space-y-0.5">
        <!-- My Profile -->
        <button (click)="onProfileClick()" 
          class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors duration-150 group text-left">
          <div class="text-slate-400 group-hover:text-blue-600 transition-colors">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <span class="text-sm font-semibold text-slate-700 group-hover:text-slate-900 transition-colors">{{ 'dashboard.profile.title' | translate }}</span>
        </button>

        <div class="h-[1px] bg-slate-100/60 mx-3 my-1"></div>

        <!-- Logout -->
        <button (click)="onLogoutClick()" 
          class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-rose-50/50 transition-colors duration-150 group text-left">
          <div class="text-slate-400 group-hover:text-rose-500 transition-colors">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </div>
          <span class="text-sm font-semibold text-slate-700 group-hover:text-rose-600 transition-colors">{{ 'header_logout' | translate }}</span>
        </button>
      </div>
    </div>
  `,
  styles: [`
    @keyframes popup {
      from {
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
    .animate-popup {
      animation: popup 0.15s cubic-bezier(0, 0, 0.2, 1);
      transform-origin: top right;
    }
  `]
})
export class HeaderProfilePopup implements OnDestroy {
  @Input() isOpen = false;
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() profile = new EventEmitter<void>();
  @Output() logout = new EventEmitter<void>();

  private router = inject(Router);
  private el = inject(ElementRef);
  private sub: Subscription;

  constructor() {
    this.sub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.close();
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.isOpen && !this.el.nativeElement.contains(event.target)) {
      // Check if click was on the trigger button (which is parent/sibling)
      // This is usually handled by stopping propagation on the trigger
      this.close();
    }
  }

  close() {
    this.isOpen = false;
    this.isOpenChange.emit(this.isOpen);
  }

  onProfileClick() {
    this.profile.emit();
    this.close();
  }

  onLogoutClick() {
    this.logout.emit();
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }
}
